<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, onMounted, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import SourceMultiAppendFormBuildingItem, {
    type Building,
} from "./SourceMultiAppendFormBuildingItem.vue"
import { useCompanySourceLimits } from "@core/composables/useCompanySourceLimits"
import type { BuildingSource } from "./SourceMultiAppendFormBuildingItemChip.vue"

// Export des types pour utilisation externe
export type { Building } from "./SourceMultiAppendFormBuildingItem.vue"
export type { BuildingSource } from "./SourceMultiAppendFormBuildingItemChip.vue"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            titles: {
                heading1: "Ajout des bâtiments et des compteurs associés",
                helper: "Veuillez renseigner les adresses et les noms des bâtiments, ainsi que les compteurs associés",
            },
            texts: {
                add: "Ajouter",
                building: "Bâtiment {count}",
            },
            maxSource: "Nombre maximum de sources atteint",
            maxSourceAlready: ({
                named,
            }: {
                named: (key: string) => string
            }) => {
                const nbCurrentSources = named("nbCurrentSources")
                const nbMaxSources = named("nbMaxSources")
                return `Vous avez atteint le nombre maximum de compteurs sur votre abonnement. Vous possédez ${nbCurrentSources} compteurs et vous pouvez en avoir ${nbMaxSources} au maximum. Veuillez nous contacter à l'adresse mail support@homeys.io pour augmenter le nombre de compteurs.`
            },
            maxSourceAfter: ({ named }: { named: (key: string) => string }) => {
                const nbMaxSources = named("nbMaxSources")
                return `L'ajout de ces compteurs vous fait atteindre le nombre maximum de compteurs inclus dans votre abonnement (${nbMaxSources} compteurs). Pour tout ajout de compteurs supplémentaires, veuillez nous contacter à l'adresse mail support@homeys.io.`
            },
            return: "Retour",
            submit: "Valider",
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================
export interface Props {
    /**
     * Liste des bâtiments avec leurs sources
     */
    modelValue: Building[]
}

interface Emits {
    /**
     * Émis lors de la soumission du formulaire (liste des bâtiments validés)
     */
    (e: "click:submit", buildings: Building[]): void
    /**
     * Émis lors du clic sur le bouton retour
     */
    (e: "click:return"): void
}

// ================================
// PROPS & EMITS
// ================================
const props = defineProps<Props>()

const emit = defineEmits<Emits>()

// ================================
// COMPOSABLES
// ================================
const {
    getMaxSubscriptionSourceCount,
    getActiveSourceCount,
    getIsLimitReached,
    getWouldExceedLimit,
} = useCompanySourceLimits()

// ================================
// ÉTAT
// ================================

/**
 * Liste interne des bâtiments
 */
const buildingsList = ref<Building[]>([])

// ================================
// COMPUTED
// ================================

/**
 * Liste de toutes les sources de tous les bâtiments (pour la blacklist)
 */
const getAllSources = computed(() => {
    return buildingsList.value
        .map((building) => building.sources)
        .flat() as BuildingSource[]
})

/**
 * Nombre total de sources dans tous les bâtiments
 */
const getTotalSourcesCount = computed(() => {
    return getAllSources.value.length
})

/**
 * Indique si la limite est atteinte avant l'ajout de nouvelles sources
 */
const getDisableMaxSourceBeforeAdd = computed(() => {
    return getIsLimitReached.value
})

/**
 * Indique si le total (sources actives + formulaire) dépasse strictement la limite
 */
const getDisableMaxSourceAfterAdd = computed(() => {
    return getWouldExceedLimit(getTotalSourcesCount.value)
})

/**
 * Vérifie si le formulaire est valide
 * Tous les bâtiments doivent avoir un nom, une adresse, une location et au moins une source
 */
const getIsFormValid = computed(() => {
    return (
        buildingsList.value.length > 0 &&
        buildingsList.value.every((building) => {
            return (
                building.id !== "" &&
                building.name !== "" &&
                building.address !== null &&
                building.location !== null &&
                building.sources.length > 0
            )
        })
    )
})

/**
 * Message d'alerte pour la limite de sources
 */
const getLimitAlertMessage = computed(() => {
    if (getDisableMaxSourceBeforeAdd.value) {
        return t("maxSourceAlready", {
            nbMaxSources: String(getMaxSubscriptionSourceCount.value),
            nbCurrentSources: String(getActiveSourceCount.value),
        })
    } else {
        return t("maxSourceAfter", {
            nbMaxSources: String(getMaxSubscriptionSourceCount.value),
        })
    }
})

// ================================
// LOGIQUE MÉTIER
// ================================

/**
 * Génère un identifiant client stable pour un bâtiment du formulaire
 */
function createMultiBuildingClientId(): string {
    if (
        typeof crypto !== "undefined" &&
        typeof crypto.randomUUID === "function"
    ) {
        return crypto.randomUUID()
    }
    return `b-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

/**
 * Assure la présence d’un `id` sur chaque bâtiment (ex. données restaurées sans id)
 */
function withStableBuildingId(building: Building): Building {
    const id = building.id?.trim()
    return id
        ? { ...building }
        : { ...building, id: createMultiBuildingClientId() }
}

/**
 * Crée un bâtiment vide avec un nom incrémenté
 */
function createEmptyBuilding(): Building {
    const buildingNumber = buildingsList.value.length + 1
    return {
        id: createMultiBuildingClientId(),
        name: t("texts.building", { count: buildingNumber }),
        address: null,
        location: null,
        sources: [],
    }
}

// ================================
// WATCHERS
// ================================

/**
 * Synchronise la liste avec le modelValue
 */
watch(
    () => props.modelValue,
    (newValue) => {
        buildingsList.value = newValue.map(withStableBuildingId)
    },
    { immediate: true, deep: true }
)

/**
 * Ajoute un nouveau bâtiment vide
 */
function onAddBuilding(): void {
    buildingsList.value.push(createEmptyBuilding())
}

/**
 * Supprime un bâtiment
 */
function onRemoveBuilding(index: number): void {
    buildingsList.value.splice(index, 1)
}

/**
 * Met à jour un bâtiment
 */
function onUpdateBuilding(index: number, building: Building): void {
    const prev = buildingsList.value[index]
    buildingsList.value.splice(index, 1, {
        ...building,
        id: building.id?.trim() || prev?.id || createMultiBuildingClientId(),
    })
}

/**
 * Soumet le formulaire
 */
function onSubmit(): void {
    if (!getIsFormValid.value) return
    emit("click:submit", buildingsList.value)
}

/**
 * Retour
 */
function onReturn(): void {
    emit("click:return")
}

// ================================
// LIFECYCLE
// ================================

/**
 * Initialise avec un bâtiment vide si la liste est vide
 */
onMounted(() => {
    if (buildingsList.value.length === 0) {
        buildingsList.value.push(createEmptyBuilding())
    }
})
</script>

<template>
    <div
        class="source-form-multi-building flex flex-col gap-6 max-w-4xl mx-auto"
    >
        <!-- ================================ -->
        <!-- HEADER -->
        <!-- ================================ -->
        <header class="flex flex-col gap-4 text-center">
            <h1 class="text-2xl font-bold text-gray-900 m-0">
                {{ t("titles.heading1") }}
            </h1>
            <p class="text-sm text-gray-600 m-0">
                {{ t("titles.helper") }}
            </p>
        </header>

        <!-- ================================ -->
        <!-- MESSAGE D'ALERTE LIMITE -->
        <!-- ================================ -->
        <BaseMessage
            v-if="getDisableMaxSourceBeforeAdd || getDisableMaxSourceAfterAdd"
            severity="error"
            :closable="false"
        >
            <template #messageicon>
                <i class="pi pi-exclamation-triangle" />
            </template>
            <div class="flex flex-col gap-1">
                <strong>{{ t("maxSource") }}</strong>
                <span>{{ getLimitAlertMessage }}</span>
            </div>
        </BaseMessage>

        <!-- ================================ -->
        <!-- LISTE DES BÂTIMENTS -->
        <!-- ================================ -->
        <div class="flex flex-col gap-4">
            <SourceMultiAppendFormBuildingItem
                v-for="(building, index) in buildingsList"
                :key="building.id"
                :model-value="building"
                :blacklist-sources="
                    getAllSources.filter((source) => {
                        // Exclure les sources du bâtiment actuel de la blacklist
                        const buildingSources = building.sources
                        return !buildingSources.some(
                            (bs) =>
                                bs.idtype === source.idtype &&
                                bs.idsource === source.idsource
                        )
                    })
                "
                :editing-by-default="!building.address"
                :disable-add-source="
                    getDisableMaxSourceBeforeAdd || getDisableMaxSourceAfterAdd
                "
                @click:remove="onRemoveBuilding(index)"
                @click:submit="onUpdateBuilding(index, $event)"
            />
        </div>

        <!-- ================================ -->
        <!-- BOUTON AJOUTER BÂTIMENT -->
        <!-- ================================ -->
        <div
            class="flex items-center justify-between gap-4 flex-wrap p-4 border border-gray-200 rounded-lg bg-gray-50"
        >
            <p class="text-sm font-medium text-gray-700 m-0">
                {{ t("texts.building", { count: buildingsList.length + 1 }) }}
            </p>
            <BaseButton
                :label="t('texts.add')"
                icon="pi pi-plus"
                text
                size="small"
                :disabled="
                    getDisableMaxSourceBeforeAdd || getDisableMaxSourceAfterAdd
                "
                @click="onAddBuilding"
            />
        </div>

        <!-- ================================ -->
        <!-- FOOTER -->
        <!-- ================================ -->
        <div
            class="flex flex-col-reverse gap-3 md:flex-row md:flex-wrap md:justify-between"
        >
            <BaseButton
                :label="t('return')"
                severity="secondary"
                outlined
                class="min-w-[10rem] w-full md:w-auto"
                @click="onReturn"
            />
            <BaseButton
                :label="t('submit')"
                class="min-w-[10rem] w-full md:w-auto"
                :disabled="!getIsFormValid"
                @click="onSubmit"
            />
        </div>
    </div>
</template>
